<template>
    <div class="tab-content no-min-width">
        <div v-if="loaded & !hasData">
            <b-row class="row-record">
                <b-col>
                    <i>No records available</i>
                </b-col>
            </b-row>
        </div>
        <div v-else class="details" :class="{ 'd-block': isMobileView }">
            <!-- left section -->
            <div :class="{ 'd-none display-on-desktop': showRightSection }">
                <div class="left-section-content">
                    <lab-result
                        ref="labResult"
                        :labId="labId"
                        @loaded="loaded = true"
                        @has-data="hasData = true"
                        @reload-lab-history="getLabHistory"
                    ></lab-result>
                </div>
            </div>
            <!-- right section -->
            <div
                id="right-section"
                :class="{
                    'd-none': !showRightSection && isMobileView,
                    'd-block': showRightSection && isMobileView
                }"
            >
                <!-- Back button for mobile -->
                <b-row
                    v-if="isMobileView && showRightSection"
                    class="mb-3 hide-on-desktop"
                >
                    <b-col>
                        <b-button
                            variant="link"
                            class="p-0"
                            @click="showRightSection = false"
                        >
                            <i class="fas fa-arrow-left"></i> Back to Results
                        </b-button>
                    </b-col>
                </b-row>

                <!-- display graph -->
                <div class="mb-3">
                    <strong>{{ tooltip }}</strong>
                </div>
                <loader v-if="isLoading" />
                <fieldset v-if="!isLoading && testResultHistory">
                    <div
                        class="statsbox bg-white"
                        v-if="plotData.length > 1 && hasValidChartData"
                    >
                        <flot-chart
                            chart-id="labTestHistory"
                            :data="plotData"
                            :xaxis-labels="dateLabels"
                            :yaxis-unit="testResultHistory[0].units"
                            :height="400"
                            :tooltipFormat="chartTooltipFormat"
                            :ranges="ranges"
                            type="line"
                            color="#000"
                        />
                    </div>
                    <div v-else class="no-graph-text">
                        <i v-if="plotData.length == 1 && hasValidChartData">
                            Not enough data available for graph</i
                        >
                        <i v-else>No data available for graph</i>
                    </div>
                </fieldset>
                <!-- display lab history -->
                <div class="mt-3">
                    <lab-history
                        v-if="!isLoading && testResultHistory"
                        :labId="labId"
                        :testResultHistory="testResultHistory"
                    ></lab-history>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Loader from "@/components/elements/loader-list";
import LabResult from "./lab-result";
import LabHistory from "./lab-history";
import PatientLabService from "@/services/patient/patient-lab";
import FlotChart from "@/components/commons/chart.vue";

const entities = require("entities");
export default {
    components: {
        Loader,
        LabResult,
        LabHistory,
        FlotChart
    },
    props: {
        labId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            testResultHistory: null,
            plotData: [],
            dateLabels: [],
            tooltip: "",
            loaded: false,
            hasData: false,
            isMobileView: false,
            showRightSection: false,
            chartTooltipFormat: "%y",
            hasValidChartData: false,
            ranges: []
        };
    },
    watch: {},
    computed: {},
    mounted() {
        this.checkMobileView();
        window.addEventListener("resize", this.checkMobileView);
        window.addEventListener("scroll", this.handleScroll);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.checkMobileView);
        window.removeEventListener("scroll", this.handleScroll);
    },
    methods: {
        checkMobileView() {
            this.isMobileView = window.innerWidth < 820;
        },
        getLabHistory(data) {
            let self = this;
            this.isLoading = true;
            this.tooltip = entities.decodeHTML(data.name);
            this.hasValidChartData = false;
            
            // Reset chart data
            this.plotData = [];
            this.dateLabels = [];
            this.ranges = [];

            if (this.isMobileView) {
                this.showRightSection = true;
            }

            PatientLabService.getLabTestResultHistory(data)
                .then(result => {
                    if (result.code == 200) {
                        self.testResultHistory = result.resource.data;
                        self.isLoading = false;

                        self.setChartData(result.resource);

                        // Wait for right section to be visible before scrolling
                        this.$nextTick(() => {
                            if (data["onClick"] && this.$refs.labResult) {
                                const element = this.$refs.labResult.$el;
                                if (element) {
                                    const rightSection = document.getElementById(
                                        "right-section"
                                    );

                                    rightSection.scrollTop = 0;

                                    if (this.isMobileView) {
                                        const tabContent = rightSection.closest(
                                            ".tab-content"
                                        );

                                        if (
                                            tabContent &&
                                            this.showRightSection
                                        ) {
                                            window.scrollTo({
                                                top: tabContent.offsetTop + 140, // Subtract 10px for spacing
                                                left: 0,
                                                behavior: "smooth"
                                            });
                                        }
                                    }
                                }
                            }
                        });
                    } else {
                        this._message("No results found", "danger");
                    }
                })
                .catch(err => {
                    this._message(err, "danger");
                });
        },
        properData(rawData = []) {
            let result = [];
            Object.keys(rawData).map(function (key) {
                result.push(Number(rawData[key]));
            });
            return result;
        },
        handleScroll() {
            if (this.isMobileView) return;

            const rightSection = document.getElementById("right-section");
            const leftSection = rightSection
                ?.closest(".details")
                ?.querySelector(".left-section-content");

            if (!rightSection || !leftSection) return;

            // Check if left section is shorter than viewport
            const leftHeight = leftSection.offsetHeight;
            const viewportHeight = window.innerHeight - 120; // Account for top header

            // If left section is shorter than viewport, no need for sticky
            if (leftHeight <= viewportHeight) {
                rightSection.classList.remove("sticky");
                return;
            }

            // Apply sticky based on scroll position
            const rect = leftSection.getBoundingClientRect();
            if (rect.top <= 120) {
                if (!rightSection.classList.contains("sticky")) {
                    rightSection.classList.add("sticky");

                    // Reset scroll position when becoming sticky
                    rightSection.scrollTop = 0;
                }
            } else {
                rightSection.classList.remove("sticky");
            }
        },
        setChartData(data) {
            let self = this;

            // Convert graphData object to array if needed
            if (
                data.graphData &&
                typeof data.graphData === "object" &&
                !Array.isArray(data.graphData)
            ) {
                // Convert object to array maintaining order by numeric keys
                data.graphData = Object.keys(data.graphData)
                    .sort((a, b) => Number(a) - Number(b)) // Sort keys numerically
                    .map(key => data.graphData[key]);
            }

            // Check if all values are valid numbers
            this.hasValidChartData = data.graphData.every(value => {
                const numericValue = parseFloat(value.toString());
                return !isNaN(numericValue);
            });

            if (data.graphData.length > 1 && this.hasValidChartData) {
                // Parse range values by getting the last item (the latest)
                const rangeStr = entities.decodeHTML(
                    self.testResultHistory[self.testResultHistory.length - 1]
                        .range
                );

                let min = 0;
                let max = null;

                // Handle range values for format "<14.0 UG/DL" or "1-14 UG/DL" or ">14.0 UG/DL"
                if (rangeStr.startsWith("<")) {
                    // Handle "<14.0 UG/DL" format
                    max = parseFloat(rangeStr.split("<")[1]);
                    min = 0;
                } else if (rangeStr.includes("-")) {
                    // Handle "1-14 UG/DL" format
                    [min, max] = rangeStr
                        .split("-")
                        .map(val =>
                            parseFloat(val.split(" ")[0].replace(",", ""))
                        );
                } else if (rangeStr.startsWith(">")) {
                    // Handle ">14.0 UG/DL" format
                    min = parseFloat(rangeStr.split(">")[1]);
                    max = null;
                }

                self.plotData = data.data.map((item, index) => [
                    index,
                    parseFloat(item.value.toString().split(" ")[0]) // Extract first number before any text
                ]);

                self.dateLabels = data.data.map((item, index) => [
                    index,
                    item.recordDate
                ]);

                // Add ranges for the chart
                self.ranges = [
                    {
                        min: min,
                        max: max,
                        color: "#CAF6BF" // Light green
                    }
                ];

                self.chartTooltipFormat = `%y ${self.testResultHistory[0].units}`;
            } else {
                self.chartTooltipFormat = "%y";
            }
        }
    }
};
</script>
<style scoped>
.details {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 35px;
    margin: 10px 0;
    position: relative;
    min-height: 500px;
}
.details > div {
    padding: 10px;
    flex-wrap: wrap;
}

/* Add divider styles */
.details > div:first-child {
    position: relative;
}

/* Right section styles */
#right-section {
    transition: all 0.3s ease;
}

#right-section.sticky {
    scroll-behavior: smooth; /* Add smooth scrolling */
}

/* Override Bootstrap tab panel styles */
:deep(.tab-pane) {
    overflow: visible !important;
}

:deep(.card-body) {
    overflow: visible !important;
}

:deep(.dashboard-list) {
    overflow: visible !important;
}

.details > div > fieldset {
    border: 1px solid #ccc;
    border-radius: 5px;
    padding: 5px;
}

.details > div > fieldset > legend {
    font-size: 100%;
    border-radius: 5px;
    padding: 5px 5px;
    width: auto;
}

.content {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    flex: 1;
    flex-wrap: wrap;
    column-gap: 7px;
}

.chart {
    min-height: 60px;
}

.row-record {
    padding: 7px 0;
    border-bottom: 1px solid #ddd;
}

.statsbox {
    width: 100%;
    padding: 10px;
}

.no-graph-text {
    margin-left: 8px;
}

/* Mobile styles */
@media (max-width: 820px) {
    .details {
        display: block;
    }

    #right-section {
        overflow-y: auto;
        overflow-x: hidden;
        background-color: #fff;
        padding: 1rem;
        width: 100% !important; /* Force width for chart */
    }

    /* Remove sticky styles for mobile */
    #right-section.sticky {
        position: fixed;
        width: 100% !important;
        height: 100vh;
        top: 0;
        right: 0;
        max-height: none;
    }
}

/* Desktop styles */
@media (min-width: 821px) {
    #right-section.sticky {
        position: fixed;
        width: calc(50% - 50px) !important;
        right: 35px;
        top: 120px;
        bottom: 50px;
        height: fit-content;
        max-height: calc(100vh - 160px);
        overflow-y: auto;
    }

    .display-on-desktop {
        display: block !important;
    }

    .hide-on-desktop {
        display: none !important;
    }

    .details > div:first-child::after {
        content: "";
        position: absolute;
        right: -17.5px; /* Half of column-gap */
        top: 0;
        bottom: 0;
        width: 1px;
        background-color: #ddd;
    }
}

/* Ensure chart container is visible */
.statsbox {
    width: 100%;
    min-height: 400px;
    visibility: visible !important;
    display: block !important;
}
</style>
